import type {
  MessageResponse,
  RequestOptions,
  VendorBusinessStatusResponse,
  VendorDashboardResponse,
  VendorOrderDetail,
  VendorManagedOrdersResponse,
} from '@cravo/core'
import { api } from '../api-client'

export const vendorDashboardApi = {
  get(options?: RequestOptions): Promise<VendorDashboardResponse> {
    return api.get<VendorDashboardResponse>('/vendor/dashboard', options)
  },

  updateBusinessStatus(businessId: string, isOnline: boolean, options?: RequestOptions): Promise<VendorBusinessStatusResponse> {
    return api.patch<VendorBusinessStatusResponse>(`/vendor/dashboard/businesses/${businessId}/status`, { isOnline }, options)
  },

  acceptOrder(orderId: string, options?: RequestOptions): Promise<MessageResponse> {
    return api.patch<MessageResponse>(`/vendor/dashboard/orders/${orderId}/accept`, undefined, options)
  },

  declineOrder(orderId: string, reason?: string, options?: RequestOptions): Promise<MessageResponse> {
    return api.patch<MessageResponse>(`/vendor/dashboard/orders/${orderId}/decline`, reason ? { reason } : undefined, options)
  },

  /**
   * Force-cancel an order that's past PENDING. The regular decline only
   * works while the order is PENDING; once accepted/preparing/etc. the
   * order can get stuck in a "dead zone" where no app can cancel it.
   * Reason is REQUIRED (backend rejects empty / <3 chars).
   */
  forceCancelOrder(orderId: string, reason: string, options?: RequestOptions): Promise<MessageResponse> {
    return api.patch<MessageResponse>(`/vendor/dashboard/orders/${orderId}/force-cancel`, { reason }, options)
  },

  listOrders(
    filter: 'current' | 'past',
    params?: { page?: number; limit?: number },
    options?: RequestOptions,
  ): Promise<VendorManagedOrdersResponse> {
    return api.get<VendorManagedOrdersResponse>('/vendor/dashboard/orders', {
      ...options,
      params: {
        filter,
        page: params?.page,
        limit: params?.limit,
        ...options?.params,
      },
    })
  },

  getOrderDetail(orderId: string, options?: RequestOptions): Promise<VendorOrderDetail> {
    return api.get<VendorOrderDetail>(`/vendor/dashboard/orders/${orderId}`, options)
  },

  updateOrderStatus(
    orderId: string,
    data: { status: 'confirmed' | 'preparing' | 'ready' | 'cancelled' | 'delivered'; reason?: string; pickupCode?: string },
    options?: RequestOptions,
  ): Promise<MessageResponse> {
    return api.patch<MessageResponse>(`/vendor/dashboard/orders/${orderId}/status`, data, options)
  },

  /**
   * Pickup-specific terminal state — vendor marks the order as customer no-show
   * when the customer never collected. Mobile parity:
   * order_status_sheet.dart:197 + dashboard repo `markNoShow`.
   */
  markOrderNoShow(
    orderId: string,
    data: { reason?: string },
    options?: RequestOptions,
  ): Promise<MessageResponse> {
    return api.patch<MessageResponse>(
      `/vendor/dashboard/orders/${orderId}/no-show`,
      data,
      options,
    )
  },

  // ─── Cancellation Requests (mobile parity) ────────────────────────
  // GET /vendor/dashboard/cancellation-requests
  // POST /vendor/dashboard/cancellation-requests/:id/approve
  // POST /vendor/dashboard/cancellation-requests/:id/reject
  listCancellationRequests(options?: RequestOptions): Promise<{
    requests: VendorCancellationRequest[]
  }> {
    return api.get<{ requests: VendorCancellationRequest[] }>(
      '/vendor/dashboard/cancellation-requests',
      options,
    )
  },

  approveCancellationRequest(
    requestId: string,
    options?: RequestOptions,
  ): Promise<MessageResponse> {
    return api.post<MessageResponse>(
      `/vendor/dashboard/cancellation-requests/${requestId}/approve`,
      undefined,
      options,
    )
  },

  rejectCancellationRequest(
    requestId: string,
    data: { reason?: string },
    options?: RequestOptions,
  ): Promise<MessageResponse> {
    return api.post<MessageResponse>(
      `/vendor/dashboard/cancellation-requests/${requestId}/reject`,
      data,
      options,
    )
  },
}

export interface VendorCancellationRequest {
  id: string
  orderId: string
  orderNumber: string
  customerName: string
  customerImage: string | null
  reason: string
  amount: number
  orderType: string
  requestedAt: string
}
